/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Grid, useTheme, useMediaQuery } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import FirstStepIcon from 'assets/icons/firstStep';
import SecondStepIcon from 'assets/icons/secondStep';
import ThirdStepIcon from 'assets/icons/thirdStep';
import {
  GoalType,
  ChallengeGoalTypes,
  ChallengeResultType,
  ICustomTheme,
} from 'interfaces';
import { Stepper } from 'components';
import { StepType } from 'components/Stepper';
import { Text as StepperText } from 'components/Stepper/styles';
import { GOAL } from 'constants/challenges';
import GenericGoalStepTop from '../GenericGoalStepTop';
import * as S from './styles';
import Conditions from './Conditions';
import SpecificGoal from './SpecificGoal';

type GoalsAndConditionsTypes = {
  challengeConditions?: any[];
  challengeResults?: Partial<ChallengeResultType>[];
  currentTab?: number;
  disabled?: boolean;
  goals?: ChallengeGoalTypes[];
  goalType: GoalType;
};

const icons: JSX.Element[] = [
  <FirstStepIcon fontSize="inherit" />,
  <SecondStepIcon fontSize="inherit" />,
  <ThirdStepIcon fontSize="inherit" />,
];

const filterBoolean = (dataArray: unknown[]) => dataArray?.filter(Boolean);

// Goals and rewards block of details page only displayed if the goal type is generic or custom. (not result)
const GoalsAndConditions = ({
  challengeConditions,
  challengeResults,
  currentTab,
  disabled = false,
  goals,
  goalType,
}: GoalsAndConditionsTypes): JSX.Element => {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const steps: StepType[] | undefined = goals?.map((goal, index) => ({
    ...goal,
    status: disabled ? 'disabled' : 'done',
    icon: icons[index],
  }));

  const [
    resultTypeConditions,
    customTypeRules,
    customTypeProfiles,
    customTypeEligibilities,
    byProfileTypeDescription,
  ] = challengeConditions || [];
  let { conditions } = resultTypeConditions || {};
  let { rules: calculationRules } = customTypeRules || {};
  let { profiles: participantsProfiles } = customTypeProfiles || {};
  let { eligibility: eligibilityRequirements } = customTypeEligibilities || {};
  const { description: additionalInformation } = byProfileTypeDescription || {};

  conditions = filterBoolean(conditions);
  calculationRules = filterBoolean(calculationRules);
  participantsProfiles = filterBoolean(participantsProfiles);
  eligibilityRequirements = filterBoolean(eligibilityRequirements);

  const genericGoalConditions =
    goalType === GOAL.GENERIC && conditions?.length > 0;
  const customGoalConditions =
    goalType === GOAL.CUSTOM &&
    (calculationRules?.length > 0 ||
      participantsProfiles?.length > 0 ||
      eligibilityRequirements?.length > 0);
  const byProfileGoalConditions =
    goalType === GOAL.BY_PROFILE && additionalInformation?.length > 0;
  const hasCondition =
    genericGoalConditions || customGoalConditions || byProfileGoalConditions;

  const customConditions: [string[] | undefined, string][] = [
    [calculationRules, 'challenges.detail.goals_and_rewards.conditions.rules'],
    [
      participantsProfiles,
      'challenges.detail.goals_and_rewards.conditions.profiles',
    ],
    [
      eligibilityRequirements,
      'challenges.detail.goals_and_rewards.conditions.eligibilities',
    ],
  ];

  const modalProps = {
    additionalInformation,
    calculationRules,
    conditions,
    eligibilityRequirements,
    participantsProfiles,
  };

  return (
    <Grid container direction={isDownMd ? 'column' : 'row'} spacing={4}>
      <S.GoalGrid
        item
        lg={!hasCondition || (goalType === GOAL.GENERIC ? 7 : 6)}
        withStepper={goalType === GOAL.GENERIC}
      >
        {goalType === GOAL.GENERIC && (
          <Stepper
            AboveStepContent={GenericGoalStepTop}
            BelowStepContent={({ step }: { step: StepType }): JSX.Element => (
              <Grid container justifyContent="center" spacing={1}>
                <Grid item>
                  <StepperText className={step.status}>
                    {steps?.[0].name}
                  </StepperText>
                </Grid>
                <Grid item>
                  <StepperText className={step.status}>
                    {step.value?.toLocaleString()}
                  </StepperText>
                </Grid>
                <Grid item>
                  <S.GoalUnit variant="body2">{steps?.[0].unit}</S.GoalUnit>
                </Grid>
                <Grid item>
                  <S.GoalSubject variant="caption">
                    {steps?.[0].subject}
                  </S.GoalSubject>
                </Grid>
              </Grid>
            )}
            disabled={disabled}
            noMobileConnector
            steps={steps}
          />
        )}

        {goalType === GOAL.CUSTOM && (
          <SpecificGoal
            disabled={disabled}
            name={goals?.[0]?.name}
            value={challengeResults?.[currentTab || 0]?.customResultValue}
            unit={goals?.[0]?.unit}
            subject={goals?.[0]?.subject}
          />
        )}
      </S.GoalGrid>

      {genericGoalConditions && (
        <Conditions
          disabled={disabled}
          goalType={GOAL.GENERIC}
          modalProps={modalProps}
        >
          {conditions.map(
            (condition: string) =>
              condition && (
                <S.Condition
                  key={condition}
                  variant={isDownSm ? 'caption' : 'body1'}
                >
                  {condition}
                </S.Condition>
              ),
          )}
        </Conditions>
      )}

      {customGoalConditions && (
        <Conditions
          disabled={disabled}
          goalType={GOAL.CUSTOM}
          modalProps={modalProps}
        >
          <div>
            {customConditions.map(([data, text]) =>
              data?.length ? (
                <div key={text}>
                  <S.ConditionSubtitle variant={isDownSm ? 'body2' : 'h4'}>
                    {t(text)}
                  </S.ConditionSubtitle>
                  {data.map(
                    (rule: string) =>
                      rule && (
                        <S.Condition
                          key={rule}
                          variant={isDownSm ? 'caption' : 'body1'}
                        >
                          {rule}
                        </S.Condition>
                      ),
                  )}
                </div>
              ) : null,
            )}
          </div>
        </Conditions>
      )}

      {byProfileGoalConditions && (
        <Conditions
          disabled={disabled}
          goalType={GOAL.BY_PROFILE}
          modalProps={modalProps}
        >
          <S.SoloCondition variant={isDownSm ? 'caption' : 'body1'}>
            {additionalInformation}
          </S.SoloCondition>
        </Conditions>
      )}
    </Grid>
  );
};

export default GoalsAndConditions;
